import { useThemeOptional } from '@/contexts/ThemeContext';
import { FONT_SCALE_CYCLE, FONT_SCALE_LABEL, nextFontScale } from '@/types';
import type { FontScale } from '@/types';

/**
 * Text-size control A / A+ / A++ · MOBILE_READABILITY slot (2026-06-06).
 *
 * Genie picks 6 JUN: A = текущие размеры (default) · A+ = ×1.15 ·
 * A++ = ×1.3. Работает на всех вьюпортах (телефон / планшет / десктоп).
 * Источник: внешний тестер #1 («текст слишком мелкий и бледный»).
 *
 * Два варианта рендера (один state · UiPrefs.fontScale persisted):
 *  - `cycle`   — компактная пилюля для topbar · клик циклит A → A+ → A++ → A
 *  - `segment` — три кнопки A / A+ / A++ для Профиля (осознанный выбор + подсказка)
 *
 * Вне <ThemeProvider> рендерит null (паттерн useThemeOptional из
 * slot_dark_theme · NavShell/screen-тесты без провайдера не падают).
 */
export function TextSizeControl({ variant = 'cycle' }: { variant?: 'cycle' | 'segment' }) {
  const ctx = useThemeOptional();
  if (!ctx) return null;
  const { fontScale, setFontScale, cycleFontScale } = ctx;

  if (variant === 'segment') {
    return (
      <div className="fs-seg" role="group" aria-label="Размер текста" data-testid="font-scale-segment">
        {FONT_SCALE_CYCLE.map((step, i) => (
          <button
            key={step}
            type="button"
            className={`fs-a${i + 1}`}
            aria-pressed={fontScale === step}
            aria-label={`Размер текста ${FONT_SCALE_LABEL[step]}`}
            onClick={() => setFontScale(step)}
          >
            {FONT_SCALE_LABEL[step]}
          </button>
        ))}
      </div>
    );
  }

  const label = FONT_SCALE_LABEL[fontScale];
  const next = FONT_SCALE_LABEL[nextFontScale(fontScale)];
  return (
    <button
      type="button"
      className="font-scale-btn"
      data-scale={fontScale satisfies FontScale}
      data-testid="font-scale-cycle"
      onClick={cycleFontScale}
      aria-label={`Размер текста: ${label}. Нажми — ${next}.`}
      title={`Размер текста: ${label}`}
    >
      {label}
    </button>
  );
}
